import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { isEqual } from "lodash";

import EventDetailsContext from "../../../context/EventDetails/EventDetails";

import AuthService from "../../../utilities/services/auth.service";

import {
  getFormattedTimezoneDate,
  getTimezoneDate,
  getIsTimeAfterStartErrorMsg,
  isTimeAfterStart,
  getNowInTimezone,
} from "../../../utilities/helpers";
import {
  getEvent,
  getAttendeesList,
  messageAttendees,
} from "../../../utilities/api";

import Card from "react-bootstrap/Card";
import Stack from "react-bootstrap/Stack";
import Button from "react-bootstrap/Button";

import { LoadingContainer } from "../../LoadingContainer";
import { Spinner } from "../../LoadingContainer/Spinner";
import { ContactForm } from "./ContactForm";
import { ConfirmationModal } from "./ConfirmationModal";

export default function ContactAttendeeWrapper({
  eventId,
  id,
  campaign,
  setIsChangesMade,
}) {
  const user = AuthService.getUser();

  const { event: eventContext } = useContext(EventDetailsContext);

  const navigate = useNavigate();

  const toOpt = [
    {
      label: "All attendees",
      value: "all",
    },
    {
      label: "Attendees by Offer Type",
      value: "by_offer_type",
    },
    {
      label: "Specific Attendees",
      value: "by_specific",
    },
  ];

  const [initialEditState, setInitialEditState] = useState();

  const [event, setEvent] = useState();

  const [offers, setOffers] = useState();

  const [attendees, setAttendees] = useState();

  const [toOption, setToOption] = useState(toOpt[0].value);

  const [replyTo, setReplyTo] = useState("");

  const [testEmail, setTestEmail] = useState("");

  const [subject, setSubject] = useState("");

  const [sendTo, setSendTo] = useState([]);

  const [title, setTitle] = useState("");

  const [message, setMessage] = useState("");

  const [sendDate, setSendDate] = useState(new Date());

  const [timeError, setTimeError] = useState(false);

  const [timeErrorMsg, setTimeErrorMsg] = useState("");

  const [choice, setChoice] = useState(1);

  const [show, setShow] = useState(false);

  const [isSendTest, setIsSendTest] = useState(false);

  const [isSending, setIsSending] = useState(false);

  const [disableTest, setDisableTest] = useState(false);

  const [isDisabled, setIsDisabled] = useState(false);

  // to show edit form after API calls are made and initial state is set
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    let cancel = false;

    setIsLoading(true);
    getEvent(eventId)
      .then((res) => {
        if (cancel) return;
        setEvent(res.data);
        setOffers(res?.data?.offers);

        getAttendeesList(eventId)
          .then((res) => {
            // remove duplicate attendees that have same user id and same offer eg. same attendee bought multiple tickets from same offer
            // fans that do not exist (have no user permissions) are considered duplicates (null === null = true)
            const noDuplicateAttendees = res?.data?.attendees.reduce(
              (acc, current) => {
                const duplicate = acc.find(
                  (attendee) =>
                    attendee.users_permissions_user?.uuid ===
                      current.users_permissions_user?.uuid &&
                    attendee.ticket?.offer?.id === current.ticket?.offer?.id,
                );

                // push returns the length of updated array while concat returns a new array
                if (!duplicate) {
                  return acc.concat([current]);
                } else {
                  return acc;
                }
              },
              [],
            );

            // remove guests
            setAttendees(
              noDuplicateAttendees.filter((attendee) => attendee.ticket),
            );

            setIsLoading(false);
          })
          .catch((err) => {
            console.error(err);
            setIsLoading(false);
          });
      })
      .catch((err) => {
        console.error(err);
        setIsLoading(false);
      });

    return () => {
      // when component unmounts because state updates campaign
      cancel = true;
    };
  }, [eventId]);

  // get campaign if editing
  useEffect(() => {
    if (id && campaign) {
      setReplyTo(campaign?.replyTo);
      setSubject(campaign?.subject);
      setMessage(campaign?.message);
      setTitle(campaign?.title);
      setToOption(campaign?.selectedBy);
      setChoice(campaign?.scheduled ? 2 : 1);
      setSendDate(
        new Date(
          getFormattedTimezoneDate(
            campaign?.scheduledTime,
            eventContext?.timezone,
          ),
        ),
      );
      // set the sendTo values
      if (campaign?.selectedBy === "by_specific") {
        setSendTo(campaign?.contacts.map((contact) => contact.uuid));
      } else if (campaign?.selectedBy === "by_offer_type") {
        const offerIds = campaign?.selectedType?.split(",");
        setSendTo(
          campaign?.event?.offers
            ?.filter((offer) => offerIds?.includes(String(offer?.id)))
            .map((offer) => offer.id), // Extract only the `id` values
        );
      }
    }
  }, [campaign, eventContext]);

  // if email is set for later date and is not editing, set send email date 3 hours ahead of today's date
  useEffect(() => {
    // choice is scheduled
    if (choice === 2) {
      // not editing
      if (!id) {
        if (eventContext) {
          const sendDate = moment(getNowInTimezone(eventContext?.timezone)).add(
            3,
            "h",
          );
          setSendDate(
            new Date(
              getFormattedTimezoneDate(sendDate, eventContext?.timezone),
            ),
          );
        }
      } else {
        // is editing
        setSendDate(initialEditState?.sendDate);
      }
    }
  }, [id, choice, eventContext, initialEditState]);

  useEffect(() => {
    if (id)
      setInitialEditState({
        replyTo,
        toOption,
        subject,
        sendTo,
        title,
        message,
        choice,
        sendDate,
      });
  }, [id, isLoading]);

  // is send date after event start
  useEffect(() => {
    // is scheduled
    if (choice === 2) {
      if (sendDate) {
        const isAfterEventStart = isTimeAfterStart(sendDate, eventContext);
        setTimeError(isAfterEventStart);
        if (isAfterEventStart) {
          setTimeErrorMsg(getIsTimeAfterStartErrorMsg(event));
        } else {
          setTimeErrorMsg("");
        }
      }
    } else {
      setTimeError(false);
      setTimeErrorMsg("");
    }
  }, [eventContext, sendDate, choice]);

  // save initial state to check if button should be disabled
  useEffect(() => {
    setIsDisabled(
      (initialEditState?.replyTo === replyTo &&
        initialEditState?.toOption === toOption &&
        isEqual(initialEditState?.sendTo, sendTo) &&
        initialEditState?.subject === subject &&
        initialEditState?.title === title &&
        initialEditState?.message === message &&
        initialEditState?.choice === choice &&
        isEqual(initialEditState?.sendDate, sendDate)) ||
        !replyTo ||
        !subject ||
        !title ||
        !message ||
        checkToValue() ||
        (!isSendTest && isSending),
    );
  }, [
    initialEditState,
    replyTo,
    toOption,
    subject,
    title,
    message,
    choice,
    sendDate,
    sendTo,
    isSending,
    isSendTest,
  ]);

  useEffect(() => {
    if (user) {
      if (!id) {
        setReplyTo(user.user.email);
      }
      setTestEmail(user.user.email);
    }
  }, []);

  // disable button depending on To field value
  const checkToValue = () => {
    if (toOption !== "all") {
      return sendTo?.length === 0;
    } else return false;
  };

  const handleChoice = (e) => {
    setChoice(Number(e.target.id));
  };

  const handleShow = () => setShow(true);

  const handleClose = () => {
    setShow(false);
    navigate(`..`);
  };

  const handleSave = () => {
    submit();
  };

  const messageDetails = async (sendTest = false) => {
    setIsSending(true);
    let data = {
      replyTo,
      title,
      subject,
      message,
      testEmail,
      toOption,
      sendTo,
      sendTest,
      eventUUID: eventId,
      choice,
      sendDate:
        choice === 1
          ? getNowInTimezone(eventContext?.timezone)
          : getTimezoneDate(sendDate, eventContext?.timezone, true).format(),
      isEditing: id ? true : false,
      campaignId: id ? id : "",
    };

    messageAttendees(data)
      .then(() => {
        if (!sendTest) {
          setIsChangesMade(true);
          handleShow();
        } else {
          setDisableTest("success");
          setIsSendTest(false);
        }
        setIsSending(false);
      })
      .catch((err) => {
        if (sendTest) setDisableTest("error");
        setIsSending(false);
        setIsSendTest(false);
        console.error(err);
      });
  };

  const sendTest = () => {
    setIsSendTest(true);
    messageDetails(true);
  };

  const submit = () => {
    messageDetails(false);
  };

  return (
    <>
      {isLoading ? (
        <LoadingContainer />
      ) : (
        <>
          <section className="wrapper">
            <header className="section-header section-heading section-heading--secondary">
              <h1>{id ? "Edit contact attendee" : "Contact attendee"}</h1>
            </header>
            <Card body className="card--sm">
              <ContactForm
                id={id}
                event={event}
                offers={offers}
                attendees={attendees}
                replyTo={replyTo}
                setReplyTo={setReplyTo}
                testEmail={testEmail}
                setTestEmail={setTestEmail}
                subject={subject}
                setSubject={setSubject}
                sendTo={sendTo}
                setSendTo={setSendTo}
                toOpt={toOpt}
                setToOption={setToOption}
                toOption={toOption}
                sendTest={sendTest}
                disableTest={disableTest}
                setDisableTest={setDisableTest}
                isSendTest={isSendTest}
                isSending={isSending}
                checkToValue={checkToValue}
                setTitle={setTitle}
                title={title}
                setMessage={setMessage}
                message={message}
                handleChoice={handleChoice}
                choice={choice}
                setDate={setSendDate}
                date={sendDate}
                timeError={timeError}
                timeErrorMsg={timeErrorMsg}
              />
            </Card>
            <Stack direction="horizontal" className="btn-group-flex">
              <Button
                variant="outline-light"
                size="lg"
                onClick={() => navigate(-1)}
              >
                Cancel
              </Button>
              <Button
                size="lg"
                className={`btn-width-md icon-button icon-button-sm icon-button--white ${!isSending ? `btn-${choice == 1 ? "send-after" : "schedule"}` : ""} `}
                disabled={isDisabled || timeError}
                onClick={handleSave}
              >
                {isSending ? (
                  <Spinner />
                ) : (
                  <>{choice === 1 ? "Send" : "Schedule"}</>
                )}
              </Button>
            </Stack>
          </section>

          <ConfirmationModal
            choice={choice}
            show={show}
            handleClose={handleClose}
          />
        </>
      )}
    </>
  );
}
